import dayjs from 'dayjs';
import values from 'lodash/values';
import { Dispatch } from 'redux';
import { Alert } from 'components/Alert';
import handlerMiddleware from 'store/handlerMiddleware';
import {
  cernerIdSelector,
  consumerIdSelector,
  profileConsumerBirthDateSelector,
  profileConsumerDisplayNameSelector,
  profileGrantorsSelector,
  profileEmailsSelector,
  profilePhonesSelector
} from 'store/profile/selectors';
import {
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import {
  notificationsChannelsSelector,
  notificationsHipaaPatientIdSelector,
  notificationsHipaaPatientNameSelector,
  notificationsProxiesDataSelector
} from './selectors';
import {
  NotificationItem,
  NotificationProxy,
  NotificationsPushTokenState,
  NotificationsHipaaAgreementObjectState
} from './types';
import { RootState } from '../types';

export const GET_NOTIFICATIONS_PREFERENCES = 'NOTIFICATIONS.GET_NOTIFICATIONS_PREFERENCES';
export const GET_NOTIFICATIONS_PREFERENCES_SUCCESS =
  'NOTIFICATIONS.GET_NOTIFICATIONS_PREFERENCES_SUCCESS';
export const GET_NOTIFICATIONS_PREFERENCES_FAIL =
  'NOTIFICATIONS.GET_NOTIFICATIONS_PREFERENCES_FAIL';
export const getNotificationsPreferences = () => ({
  type: GET_NOTIFICATIONS_PREFERENCES,
  payload: {
    client: 'notifications',
    request: {
      method: 'get',
      url: `/consumers/consumer/preferences`
    }
  }
});

export const SET_HIPAA_PATIENT = 'NOTIFICATIONS.SET_HIPAA_PATIENT';
export const setHipaaPatient = (patient: NotificationProxy) => ({
  type: SET_HIPAA_PATIENT,
  payload: patient
});

export const TOGGLE_NOTIFICATIONS_PREFERENCE = 'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_PREFERENCE';
export const TOGGLE_NOTIFICATIONS_PREFERENCE_SUCCESS =
  'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_PREFERENCE_SUCCESS';

export const TOGGLE_NOTIFICATIONS_PREFERENCE_FAIL =
  'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_PREFERENCE_FAIL';
export const toggleNotificationsPreference = (
  item: NotificationItem,
  type: 'channels' | 'categories'
) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const channels = values(notificationsChannelsSelector(state));
  const phoneNumber = profilePhonesSelector(state)?.[0]?.value;
  const emailAddress = profileEmailsSelector(state)?.[0]?.value;
  const noChannelsEnabled = !channels.some((c: NotificationItem) => !!c.enabled);
  const newItem: { key: string; enabled: boolean; address?: string } = {
    key: item.key,
    enabled: !item.enabled
  };

  // Start HIPAA flow if this is first channel to be turned on
  if (type === 'channels' && noChannelsEnabled) {
    const proxies = notificationsProxiesDataSelector(state);
    const cernerId = cernerIdSelector(state);
    const userIsNotAuthorized =
      proxies && cernerId && proxies[cernerId] && !proxies[cernerId].authorized;
    if (userIsNotAuthorized) {
      dispatch({ type: SET_HIPAA_PATIENT, payload: proxies[cernerId] });
      return;
    }
  }

  if (newItem.key === 'SMS' && newItem.enabled) {
    newItem.address = phoneNumber;
  }
  if (newItem.key === 'EMAIL' && newItem.enabled) {
    newItem.address = emailAddress;
  }

  dispatch({
    type: TOGGLE_NOTIFICATIONS_PREFERENCE,
    payload: {
      client: 'notifications',
      request: {
        method: 'patch',
        url: `/consumers/consumer/preferences`,
        data: {
          [type]: [newItem]
        }
      },
      key: item.key,
      type
    }
  });
};

export const TOGGLE_NOTIFICATIONS_CHANNELS_OFF = 'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_CHANNELS_OFF';
export const TOGGLE_NOTIFICATIONS_CHANNELS_OFF_SUCCESS =
  'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_CHANNELS_OFF_SUCCESS';
export const TOGGLE_NOTIFICATIONS_CHANNELS_OFF_FAIL =
  'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_CHANNELS_OFF_FAIL';
export const toggleNotificationsChannelsOff = () => (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const channelsByKey = notificationsChannelsSelector(state);
  const channels = values(channelsByKey).map(c => ({
    ...c,
    enabled: false
  }));

  dispatch({
    type: TOGGLE_NOTIFICATIONS_CHANNELS_OFF,
    payload: {
      client: 'notifications',
      request: {
        method: 'patch',
        url: `/consumers/consumer/preferences`,
        data: {
          channels
        }
      }
    }
  });
};

export const GET_NOTIFICATIONS_PROXIES = 'NOTIFICATIONS.GET_NOTIFICATIONS_PROXIES';
export const GET_NOTIFICATIONS_PROXIES_SUCCESS = 'NOTIFICATIONS.GET_NOTIFICATIONS_PROXIES_SUCCESS';
export const GET_NOTIFICATIONS_PROXIES_FAIL = 'NOTIFICATIONS.GET_NOTIFICATIONS_PROXIES_FAIL';
export const getNotificationsProxies = () => {
  return {
    type: GET_NOTIFICATIONS_PROXIES,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        url: getDispatcherUrl(
          DispatcherCategoryService.HEALTH_RECORDS,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.HIPAA,
          DispatcherEndpoint.ELIGIBILITY
        )
      }
    }
  };
};

export const TOGGLE_NOTIFICATIONS_PROXY = 'NOTIFICATIONS.TOGGLE_NOTIFICATIONS_PROXY';
export const toggleNotificationsProxy = (patientId: string, isRevokingGrantor: boolean) => ({
  type: TOGGLE_NOTIFICATIONS_PROXY,
  payload: { patientId, isRevokingGrantor }
});

export const GET_HIPAA_AGREEMENT = 'NOTIFICATIONS.GET_HIPAA_AGREEMENT';
export const GET_HIPAA_AGREEMENT_SUCCESS = 'NOTIFICATIONS.GET_HIPAA_AGREEMENT_SUCCESS';
export const GET_HIPAA_AGREEMENT_FAIL = 'NOTIFICATIONS.GET_HIPAA_AGREEMENT_FAIL';

export const getHipaaAgreement = (documentType: 'authorization' | 'revocation') => (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const cernerId = cernerIdSelector(state);
  const patientId = notificationsHipaaPatientIdSelector(state);
  const proxyName = profileConsumerDisplayNameSelector(state);
  const signature = proxyName;
  const patientName = notificationsHipaaPatientNameSelector(state);
  const grantors = profileGrantorsSelector(state);
  const grantorInfo = grantors && grantors.find(g => g.cernerPersonId === patientId);
  let medicalRecordNumber = '';
  let patientBirthDate = '';

  if (cernerId === patientId) {
    patientBirthDate = profileConsumerBirthDateSelector(state);
    medicalRecordNumber = consumerIdSelector(state);
  } else {
    patientBirthDate = grantorInfo ? grantorInfo.dateOfBirth : '';
    medicalRecordNumber = grantorInfo ? grantorInfo.consumerId : '';
  }

  dispatch({
    type: GET_HIPAA_AGREEMENT,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        url: getDispatcherUrl(
          DispatcherCategoryService.HEALTH_RECORDS,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.HIPAA,
          DispatcherEndpoint.PREVIEW
        ),
        data: {
          documentType,
          location: '',
          medicalRecordNumber,
          patientBirthDate,
          patientId,
          patientName,
          proxyName,
          receivedThrough: 'Electronic',
          relationshipToPatient: '',
          signature,
          signatureDateTime: dayjs().format('MM/DD/YYYY'),
          version: ''
        }
      }
    }
  });
};

export const SIGN_HIPAA_AGREEMENT = 'NOTIFICATIONS.SIGN_HIPAA_AGREEMENT';
export const SIGN_HIPAA_AGREEMENT_SUCCESS = 'NOTIFICATIONS.SIGN_HIPAA_AGREEMENT_SUCCESS';
export const SIGN_HIPAA_AGREEMENT_FAIL = 'NOTIFICATIONS.SIGN_HIPAA_AGREEMENT_FAIL';
export const signHipaaAgreement = (
  documentType: string,
  patientID: string,
  data: NotificationsHipaaAgreementObjectState
) => {
  const headers = { 'x-patient-id': patientID, 'x-dfd-consumer-id': data.medicalRecordNumber };
  const dataWithType = { ...data, documentType };
  return {
    type: SIGN_HIPAA_AGREEMENT,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        url: getDispatcherUrl(
          DispatcherCategoryService.HEALTH_RECORDS,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.HIPAA
        ),
        data: dataWithType,
        headers
      }
    }
  };
};

export const SET_PUSH_TOKEN_INFO = 'NOTIFICATIONS.SET_PUSH_TOKEN_INFO';
export const setPushTokenInfo = ({ tokenType, tokenAddress }: NotificationsPushTokenState) => ({
  type: SET_PUSH_TOKEN_INFO,
  payload: {
    tokenType,
    tokenAddress
  }
});

export const handleToggleNotificationsPreferenceFail = () => {
  Alert.alert(
    'Unable to Update Preferences',
    `We are having trouble updating your notification preferences. Please try again or contact support.`
  );
};

// Register handlers
handlerMiddleware.registerHandler(
  TOGGLE_NOTIFICATIONS_PREFERENCE_FAIL,
  handleToggleNotificationsPreferenceFail
);
